import React, { useRef, useState, useEffect } from "react";
import "../common/common.css";
import { useAuth } from "../auth/AuthContext";
import {
  getMyNotifications,
  markNotificationRead,
} from "../../api/notificationsApi";
import { useSearch } from "../search/SearchContext";
import QueryAutocomplete from "../search/QueryAutocomplete";
import { Link } from "react-router-dom";
import PreferencesModal from "../preferences/PreferencesModal";
import { useLogs } from "../logs/LogsContext";
import useGeneralSettings from "../preferences/useGeneralSettings";
import { useTranslation } from "react-i18next";
import en from "../../locales/en.json";

/** Small reusable avatar + dropdown menu for authenticated user */
export function UserMenu() {
  const {
    user,
    logout,
    language: currentLanguage,
    setLanguage,
    token,
  } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const ref = useRef(null);
  const { t, i18n } = useTranslation();

  const supportedLangs = [
    { code: "en", label: t("language.en"), flag: "🇬🇧" },
    { code: "fr", label: t("language.fr"), flag: "🇫🇷" },
    { code: "tr", label: t("language.tr"), flag: "🇹🇷" },
  ];

  // Generic mapping for common language codes to emoji flags. Expand as needed for
  // your 20 languages. If a code is not found, fallback to the language code.
  const flagForCode = (code) => {
    const map = {
      en: "🇬🇧",
      fr: "🇫🇷",
      tr: "🇹🇷",
      es: "🇪🇸",
      de: "🇩🇪",
      it: "🇮🇹",
      pt: "🇵🇹",
      ru: "🇷🇺",
      zh: "🇨🇳",
      ja: "🇯🇵",
      ko: "🇰🇷",
      nl: "🇳🇱",
      sv: "🇸🇪",
      no: "🇳🇴",
      da: "🇩🇰",
      fi: "🇫🇮",
      pl: "🇵🇱",
      ar: "🇸🇦",
      he: "🇮🇱",
      hi: "🇮🇳",
    };
    return map[code] || code;
  };

  // Load pending (unread) notifications for the current user so the
  // avatar badge and dropdown reflect server state.
  useEffect(() => {
    if (!user || !token) {
      setNotifications([]);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const items = await getMyNotifications(token);
        if (!cancelled) {
          setNotifications(items || []);
        }
      } catch {
        if (!cancelled) {
          setNotifications([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, token]);

  if (!user) {
    return (
      <div className="user-menu" title={t("user.notSignedIn")}>
        <div className="avatar-circle anonymous">?</div>
      </div>
    );
  }

  const initial = (user.name || user.email || "?").trim()[0]?.toUpperCase();

  // Debug logs removed.

  // Determine a safe role label to display. Look for roles in these places
  // (in order): server-provided roles, JWT-decoded claims (common keys),
  // or fallback to isAdmin -> Admin, otherwise 'User'. We handle strings,
  // arrays, and comma-separated role lists.
  let roleLabel = "";
  let roleTitle = "";

  function normalizeToArray(val) {
    if (!val && val !== 0) return [];
    if (Array.isArray(val)) return val.map((v) => String(v));
    if (typeof val === "string")
      return val
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    return [String(val)];
  }

  // 1) server-provided roles (preferred)
  const serverRoles = user?.server?.roles;
  if (serverRoles) {
    const arr = normalizeToArray(serverRoles);
    if (arr.length) {
      roleLabel = arr[0];
      roleTitle = arr.join(", ");
    }
  }

  // 2) check JWT-decoded claims if no server roles found
  if (!roleLabel && user?.claims) {
    const c = user.claims;
    let found = null;

    // Helper: if claims is an array, try to extract role values from objects
    function extractFromArray(arr) {
      // array of plain strings
      if (arr.every((i) => typeof i === "string")) return arr;
      // array of objects: look for keys that indicate role and pull their value
      const vals = [];
      for (const it of arr) {
        if (!it) continue;
        if (typeof it === "string") {
          vals.push(it);
          continue;
        }
        // common field names that may contain the claim type/key
        const typeField =
          it.type || it.Type || it.key || it.name || it.claimType || null;
        const valueField =
          it.value || it.Value || it.claimValue || it.claim || it.val || null;
        if (
          typeField &&
          typeof typeField === "string" &&
          typeField.toLowerCase().includes("role")
        ) {
          if (valueField) vals.push(valueField);
        } else {
          // if object has property names that include 'role', use those
          for (const k of Object.keys(it)) {
            if (k.toLowerCase().includes("role") && it[k]) {
              vals.push(it[k]);
              break;
            }
          }
        }
      }
      return vals;
    }

    if (Array.isArray(c)) {
      const arrVals = extractFromArray(c);
      if (arrVals && arrVals.length) found = arrVals;
    } else if (typeof c === "object") {
      // Common claim keys that may contain roles
      const keysToCheck = [
        "roles",
        "role",
        "http://schemas.microsoft.com/ws/2008/06/identity/claims/role",
        "roles[]",
      ];
      for (const k of keysToCheck) {
        if (Object.prototype.hasOwnProperty.call(c, k) && c[k]) {
          found = c[k];
          break;
        }
      }
      // If not found by common keys, try any claim key containing 'role'
      if (!found) {
        const anyKey = Object.keys(c).find(
          (k) => k && k.toLowerCase().includes("role")
        );
        if (anyKey) found = c[anyKey];
      }
    } else {
      // fallback: claims may be a single string
      found = c;
    }

    const arr = normalizeToArray(found);
    if (arr.length) {
      roleLabel = arr[0];
      roleTitle = arr.join(", ");
    }
  }

  // 3) fallback
  if (!roleLabel) {
    roleLabel = user.isAdmin ? "Admin" : "User";
    roleTitle = roleLabel;
  }

  // Always present a simple badge to users: either 'Administrator' or 'User'.
  // We intentionally hide internal role names (e.g., DIXI_ADMIN).
  roleLabel = user.isAdmin ? "Administrator" : "User";
  roleTitle = roleLabel;

  const unreadCount = notifications ? notifications.length : 0;

  const handleNotificationClick = async (n) => {
    const id = n.id || n.Id;
    if (!id || !token) return;
    try {
      await markNotificationRead(token, id);
      setNotifications((prev) => prev.filter((x) => (x.id || x.Id) !== id));
    } catch {
      // ignore individual notification errors in the top bar
    }
  };

  return (
    <div className="user-menu" ref={ref}>
      <div className="avatar-wrapper">
        <button
          className="avatar-circle"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={open}
          title={user.email}
        >
          {initial || "?"}
        </button>
        {unreadCount > 0 && <span className="avatar-badge">{unreadCount}</span>}
      </div>
      {open && (
        <div className="user-dropdown um-dropdown" role="menu">

          {/* Header */}
          <div className="app-menu-header">
            <div className="um-header-avatar">{initial}</div>
            <div className="app-menu-header-text" style={{ marginLeft: 12 }}>
              <div className="app-menu-username" title={user.name}>{user.name}</div>
              <div className="um-email" title={user.email}>{user.email}</div>
              <span className="app-menu-role-badge" title={roleTitle}>{roleLabel}</span>
            </div>
          </div>

          {/* Notifications */}
          <div className="app-menu-section">
            <div className="app-menu-section-title um-notif-title">
              <span>{t("user.notifications") || "Notifications"}</span>
              <Link to="/notifications" onClick={() => setOpen(false)} className="um-notif-link">
                {t("user.viewAll", { defaultValue: "View all" })} →
              </Link>
            </div>
            <div style={{ maxHeight: 160, overflowY: "auto" }}>
              {notifications.length === 0 ? (
                <div className="um-empty">{t("user.noNotifications") || "No notifications"}</div>
              ) : (
                notifications.map((n) => (
                  <button key={n.id || n.Id} type="button" className="menu-item um-notif-item" onClick={() => handleNotificationClick(n)}>
                    <span className="app-menu-label" style={{ display: "block" }}>{n.title || n.Title}</span>
                    {(n.message || n.Message) && (
                      <span className="app-menu-sub" style={{ display: "block", textAlign: "left" }}>{n.message || n.Message}</span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="menu-sep" />

          {/* Settings */}
          <div className="app-menu-section">
            <div className="app-menu-section-title">{t("user.settings", { defaultValue: "Settings" })}</div>
            <button className="menu-item app-menu-item" role="menuitem" onClick={() => alert(t("user.profileSettings") + " coming soon")}>
              <span className="app-menu-icon">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                  <circle cx="9" cy="6.5" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M3 15c0-3.314 2.686-5 6-5s6 1.686 6 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </span>
              <span className="app-menu-item-text">
                <span className="app-menu-label">{t("user.profileSettings")}</span>
              </span>
            </button>
            <button className="menu-item app-menu-item" role="menuitem" onClick={() => { setPrefsOpen(true); setOpen(false); }}>
              <span className="app-menu-icon">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                  <rect x="2" y="4" width="14" height="1.5" rx=".75" fill="currentColor"/>
                  <rect x="2" y="8.25" width="14" height="1.5" rx=".75" fill="currentColor"/>
                  <rect x="2" y="12.5" width="14" height="1.5" rx=".75" fill="currentColor"/>
                  <circle cx="6" cy="4.75" r="1.5" fill="#fff" stroke="currentColor" strokeWidth="1.2"/>
                  <circle cx="11" cy="9" r="1.5" fill="#fff" stroke="currentColor" strokeWidth="1.2"/>
                  <circle cx="7" cy="13.25" r="1.5" fill="#fff" stroke="currentColor" strokeWidth="1.2"/>
                </svg>
              </span>
              <span className="app-menu-item-text">
                <span className="app-menu-label">{t("user.preferences")}</span>
              </span>
            </button>
          </div>

          <div className="menu-sep" />

          {/* Language */}
          <div className="app-menu-section">
            <div className="app-menu-section-title">{t("language.label")}</div>
            {(() => {
              const activeLang = currentLanguage || i18n.language || "en";
              const allCodes = Object.keys(i18n?.options?.resources || {}).length
                ? Object.keys(i18n.options.resources)
                : supportedLangs.map((s) => s.code);
              return (
                <>
                  <button
                    type="button"
                    className="menu-item app-menu-item"
                    onClick={() => setLangOpen((v) => !v)}
                  >
                    <span className="app-menu-icon" style={{ fontSize: 16 }}>
                      {flagForCode(activeLang)}
                    </span>
                    <span className="app-menu-item-text">
                      <span className="app-menu-label">{t(`language.${activeLang}`) || activeLang}</span>
                    </span>
                    <span className="app-menu-chevron" style={{ transform: langOpen ? "rotate(90deg)" : "none", transition: "transform .15s" }}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                        <path d="M5 3L9 7L5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  </button>
                  {langOpen && (
                    <div className="um-lang-list">
                      {allCodes.map((code) => (
                        <button
                          key={code}
                          type="button"
                          className={`menu-item um-lang-option${code === activeLang ? " um-lang-active" : ""}`}
                          onClick={() => {
                            try { setLanguage?.(code); } catch {}
                            try { i18n.changeLanguage(code); localStorage.setItem("locale", code); } catch {}
                            setLangOpen(false);
                          }}
                        >
                          <span style={{ fontSize: 15, lineHeight: 1 }}>{flagForCode(code)}</span>
                          <span>{t(`language.${code}`) || code}</span>
                          {code === activeLang && (
                            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ marginLeft: "auto" }} aria-hidden="true">
                              <path d="M2 7L5.5 10.5L11 3" stroke="#3b5bdb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          <div className="menu-sep" />

          {/* Sign out */}
          <div style={{ padding: "4px 8px 8px" }}>
            <button
              className="menu-item um-signout"
              role="menuitem"
              onClick={() => { logout(); setOpen(false); }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
                <path d="M6 2H3C2.448 2 2 2.448 2 3V13C2 13.552 2.448 14 3 14H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <polyline points="10,5 13,8 10,11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                <line x1="13" y1="8" x2="6" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              {t("user.logout")}
            </button>
          </div>

        </div>
      )}
      <PreferencesModal
        open={prefsOpen}
        onClose={() => setPrefsOpen(false)}
        initialTab="ai"
      />
    </div>
  );
}

export function TopBar({ showSearch = true }) {
  const { t, i18n } = useTranslation();
  const logs = useLogs?.() ?? null;
  const general = useGeneralSettings();
  const {
    query,
    runSearch,
    loading,
    error,
    toggleSavedFor,
    saveSearch,
    removeSaved,
    isSaved,
    interpreted,
    interpreter,
    lastSearchExpression,
    workspaceScope,
    setWorkspaceScope,
  } = useSearch();
  // Show the actual LLM-generated filter when available, otherwise fall back to local interpretation
  const displayedFilter = lastSearchExpression || interpreted;
  const searchBoxRef = useRef(null);
  const [showInterpreted, setShowInterpreted] = useState(true);
  const [dismissedError, setDismissedError] = useState(false);
  const [saveNameOpen, setSaveNameOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const saveNameRef = useRef(null);
  const [useOpenAI, setUseOpenAI] = useState(() => {
    const v =
      (typeof localStorage !== "undefined" &&
        localStorage.getItem("use_openai")) ||
      "";
    return v ? ["1", "true", "yes", "on"].includes(v.toLowerCase()) : false;
  });
  const [useServerLLM, setUseServerLLM] = useState(() => {
    const v =
      (typeof localStorage !== "undefined" &&
        localStorage.getItem("use_server_llm")) ||
      "";
    return v ? ["1", "true", "yes", "on"].includes(v.toLowerCase()) : false;
  });
  const [prefsOpen, setPrefsOpen] = useState(false);
  const aiEnabled = useOpenAI || useServerLLM;
  const effectiveInterpreter = interpreter ?? (interpreted ? "local" : null);
  const isPreviewSource = !interpreter && !!interpreted; // shows when it's from live preview

  // When interpretation changes (new query), show the tooltip again
  useEffect(() => {
    setShowInterpreted(true);
  }, [displayedFilter]);

  // Click-away to dismiss the interpreted tooltip
  useEffect(() => {
    if (!showInterpreted) return;
    const onDoc = (e) => {
      const el = searchBoxRef.current;
      if (el && !el.contains(e.target)) setShowInterpreted(false);
    };
    const onKey = (e) => e.key === "Escape" && setShowInterpreted(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [showInterpreted]);

  // Click-away to dismiss save-name popover
  useEffect(() => {
    if (!saveNameOpen) return;
    const onDoc = (e) => {
      if (saveNameRef.current && !saveNameRef.current.closest?.("div")?.contains(e.target)) {
        setSaveNameOpen(false);
      }
    };
    const timer = setTimeout(() => document.addEventListener("mousedown", onDoc), 0);
    return () => { clearTimeout(timer); document.removeEventListener("mousedown", onDoc); };
  }, [saveNameOpen]);

  // Log fresh errors when they appear once
  useEffect(() => {
    if (error && !dismissedError && logs) {
      const msg = String(error).slice(0, 4000);
      // Avoid spamming logs: only log if different from last entry or older than 5s
      const entries = logs.entries || [];
      const last = entries[entries.length - 1];
      const tooSoon =
        last &&
        last.level === "error" &&
        last.message === msg &&
        Date.now() - new Date(last.ts).getTime() < 5000;
      if (!tooSoon) {
        logs.log("error", msg, { context: "search", query });
      }
    }
  }, [error, dismissedError, logs, query]);

  // If the error changes, allow it to show again
  useEffect(() => {
    if (error !== undefined) {
      setDismissedError(false);
    }
  }, [error]);

  const onKeyDown = (e) => {
    const requireCmd = general.requireCmdEnterToSearch;
    if (e.key === "Enter") {
      if (requireCmd) {
        if (e.metaKey) runSearch(query);
      } else {
        runSearch(query);
      }
    }
  };

  function resolveWithFallback(key) {
    try {
      const v = t(key);
      // i18next returns the key itself when missing; detect and fallback to English
      if (typeof v === "string" && v.indexOf(".") > -1 && v === key) {
        // try i18n explicit English fallback first
        const enVal = i18n.t(key, { lng: "en" });
        if (typeof enVal === "string" && enVal !== key) return enVal;
        // final fallback: read directly from bundled English JSON
        try {
          const parts = key.split(".");
          let cur = en;
          for (const p of parts) {
            if (!cur || typeof cur !== "object" || !(p in cur)) {
              cur = null;
              break;
            }
            cur = cur[p];
          }
          if (typeof cur === "string") return cur;
        } catch {}
        return key;
      }
      return v;
    } catch {
      try {
        const enVal = i18n.t(key, { lng: "en" });
        if (typeof enVal === "string" && enVal !== key) return enVal;
        // attempt direct English JSON lookup
        const parts = key.split(".");
        let cur = en;
        for (const p of parts) {
          if (!cur || typeof cur !== "object" || !(p in cur)) {
            cur = null;
            break;
          }
          cur = cur[p];
        }
        if (typeof cur === "string") return cur;
        return key;
      } catch {
        return key;
      }
    }
  }

  return (
    <header className="top-bar">
      {showSearch && (
        <div
          className="search-wrapper"
          style={{ minWidth: 0, position: "relative" }}
          ref={searchBoxRef}
        >
          <QueryAutocomplete
            className="search-input"
            placeholder={
              aiEnabled
                ? resolveWithFallback("search.placeholderAi")
                : resolveWithFallback("search.placeholderNoAi")
            }
            onKeyDown={onKeyDown}
            disabled={loading}
          />
          <button
            className="go-btn"
            aria-label={t("search.run")}
            onClick={() => runSearch(query)}
            disabled={loading}
          >
            {loading ? "…" : "➜"}
          </button>
          {/* Scope the search to what I have filed — the personal layer as a filter on the ordinary
              search, not a separate index. Sits beside the AI toggle because both change what the SAME
              query means, and both need to be visible while you read the results. */}
          <button
            className={`scope-btn ${workspaceScope ? "active" : ""}`}
            aria-pressed={workspaceScope}
            aria-label={t("search.workspaceScope", { defaultValue: "Search only my workspace" })}
            title={
              workspaceScope
                ? t("search.workspaceScopeOnTitle", {
                    defaultValue: "Searching only documents you have filed. Click to search everything.",
                  })
                : t("search.workspaceScopeOffTitle", {
                    defaultValue: "Searching the whole archive. Click to search only your own folders.",
                  })
            }
            onClick={() => setWorkspaceScope(!workspaceScope)}
            disabled={loading}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 3.6-6 8-6s8 2 8 6" />
            </svg>
          </button>
          <button
            className={`ai-btn ${aiEnabled ? "active" : ""}`}
            aria-pressed={aiEnabled}
            aria-label={
              aiEnabled ? t("search.aiDisable") : t("search.aiEnable")
            }
            title={aiEnabled ? t("search.aiOnTitle") : t("search.aiOffTitle")}
            onClick={() => {
              const next = !aiEnabled;
              setUseOpenAI(next);
              setUseServerLLM(next);
              try {
                if (typeof localStorage !== "undefined") {
                  localStorage.setItem("use_openai", next ? "true" : "false");
                  localStorage.setItem("use_server_llm", next ? "true" : "false");
                }
              } catch {}
            }}
          >
            AI
          </button>
          <span style={{ position: "relative" }}>
            <button
              className="star-btn"
              aria-label={
                isSaved ? t("search.removeSaved") : t("search.saveSearch")
              }
              title={isSaved ? t("search.removeSaved") : t("search.saveSearch")}
              onClick={() => {
                if (isSaved) {
                  removeSaved(query);
                } else {
                  setSaveName("");
                  setSaveNameOpen(true);
                  setTimeout(() => saveNameRef.current?.focus(), 50);
                }
              }}
              disabled={!query}
            >
              {isSaved ? "★" : "☆"}
            </button>
            {saveNameOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 6px)",
                  right: 0,
                  zIndex: 30,
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: 12,
                  boxShadow: "0 6px 18px rgba(0,0,0,0.10)",
                  minWidth: 240,
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                  {t("search.saveViewTitle")}
                </div>
                <input
                  ref={saveNameRef}
                  type="text"
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  placeholder={t("search.saveViewPlaceholder")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      saveSearch(query, saveName.trim() || undefined);
                      setSaveNameOpen(false);
                    } else if (e.key === "Escape") {
                      setSaveNameOpen(false);
                    }
                  }}
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    border: "1px solid #d1d5db",
                    borderRadius: 6,
                    fontSize: 13,
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
                <div style={{ display: "flex", gap: 6, marginTop: 8, justifyContent: "flex-end" }}>
                  <button
                    onClick={() => setSaveNameOpen(false)}
                    style={{
                      padding: "4px 10px",
                      fontSize: 12,
                      borderRadius: 5,
                      border: "1px solid #d1d5db",
                      background: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    onClick={() => {
                      saveSearch(query, saveName.trim() || undefined);
                      setSaveNameOpen(false);
                    }}
                    style={{
                      padding: "4px 10px",
                      fontSize: 12,
                      borderRadius: 5,
                      border: "none",
                      background: "#3b82f6",
                      color: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    {t("search.saveBtn")}
                  </button>
                </div>
              </div>
            )}
          </span>
          {error && !dismissedError && (
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 6px)",
                left: 0,
                right: 0,
                zIndex: 20,
                background: "#fff5f5",
                border: "1px solid #ffcccc",
                borderRadius: 8,
                padding: 10,
                boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                color: "#8f0018",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "flex-start", gap: 8 }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      marginBottom: 4,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={String(error)}
                  >
                    {t("search.errorTitle")}
                  </div>
                  <div
                    style={{
                      maxHeight: 80,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      fontFamily:
                        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                      fontSize: 12,
                    }}
                    title={String(error)}
                  >
                    {String(error)}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <Link
                      to="/system-logs"
                      onClick={() => setDismissedError(true)}
                      className="link-btn"
                      style={{ textDecoration: "none" }}
                    >
                      {t("search.viewLogs")} →
                    </Link>
                  </div>
                </div>
                <button
                  className="link-btn"
                  aria-label={t("common.dismiss")}
                  title={t("common.dismiss")}
                  onClick={() => setDismissedError(true)}
                >
                  ×
                </button>
              </div>
            </div>
          )}
          {/* Interpreted tooltip anchored to the search box; dismiss on click-away */}
          {displayedFilter &&
            showInterpreted &&
            !error &&
            general.showInterpretedPreview && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 6px)",
                  left: 0,
                  right: 0,
                  zIndex: 19,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    color: "#666",
                    background: "#fff",
                    padding: "4px 8px",
                    borderRadius: 8,
                    border: "1px solid #eee",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                  title={displayedFilter}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                    }}
                  >
                    Filter: {displayedFilter}
                  </span>
                  {effectiveInterpreter && (
                    <span
                      style={{ fontSize: 10, color: "#666", flexShrink: 0 }}
                    >
                      Interpreted by:
                    </span>
                  )}
                  {effectiveInterpreter && (
                    <span
                      title={
                        effectiveInterpreter === "openai"
                          ? "Interpreted by OpenAI"
                          : effectiveInterpreter === "server"
                          ? "Interpreted by Server LLM"
                          : isPreviewSource
                          ? "Interpreted locally (preview)"
                          : "Interpreted locally"
                      }
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: 0.3,
                        textTransform: "uppercase",
                        padding: "2px 6px",
                        borderRadius: 999,
                        border: "1px solid #ddd",
                        flexShrink: 0,
                        background:
                          effectiveInterpreter === "openai"
                            ? "#eef6ff"
                            : effectiveInterpreter === "server"
                            ? "#f9f5ff"
                            : "#f6f8fa",
                        color:
                          effectiveInterpreter === "openai"
                            ? "#0969da"
                            : effectiveInterpreter === "server"
                            ? "#6f42c1"
                            : "#57606a",
                      }}
                    >
                      {effectiveInterpreter === "openai"
                        ? "OpenAI"
                        : effectiveInterpreter === "server"
                        ? "Server LLM"
                        : "Local"}
                    </span>
                  )}
                  <button
                    className="link-btn"
                    onClick={() => navigator.clipboard.writeText(displayedFilter)}
                  >
                    Copy
                  </button>
                  <button
                    className="link-btn"
                    aria-label="Dismiss"
                    title="Dismiss"
                    onClick={() => setShowInterpreted(false)}
                  >
                    ×
                  </button>
                </div>
              </div>
            )}
        </div>
      )}
      <PreferencesModal
        open={prefsOpen}
        onClose={() => setPrefsOpen(false)}
        initialTab="ai"
      />
    </header>
  );
}

// Minimal header for pages that don't provide SearchContext (e.g., Preferences)
export function HeaderBar() {
  return (
    <header className="top-bar">
      <div style={{ flex: 1 }} />
    </header>
  );
}
